import { FC, useState } from 'react'
import { Button, Image, Input, Skeleton, Space, Tooltip, Typography } from 'antd'
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { useLazyQuery } from '@apollo/client'
import { translate } from '../../../core'
import { Combo, ImageUpload } from '../../../core/component'
import { resolvePathFile } from '../../../core/until'
import { GetItemsForUpdateImages } from '../../inventory/graphql/item.graphql'
import { Item } from '../../inventory/type'
import { QueryPageableRequest, QueryPageableResponse } from '../../../core/type'
import { ItemFindRequest } from '../../inventory/type/item-query'
import { CarouselGroup, CarouselSubSlide } from '../type/carousel'
import { ItemType } from '../../inventory/type'

type Props = {
  group?: CarouselGroup
  value?: CarouselSubSlide[]
  onChange?: (subSlides: CarouselSubSlide[]) => void
}

const REQUIRED_COUNT = 3

export const CarouselSubSlideSection: FC<Props> = ({ group, value = [], onChange }) => {
  const [uploadingIndexes, setUploadingIndexes] = useState<Record<number, boolean>>({})
  const itemTypes = group === CarouselGroup.Watch
    ? [ItemType.Watch]
    : group === CarouselGroup.Jewelry
      ? [ItemType.Jewelery]
      : undefined
  const [itemsFind] = useLazyQuery<
    QueryPageableResponse<Item, 'itemsFind'>,
    QueryPageableRequest<ItemFindRequest>
  >(GetItemsForUpdateImages)

  const updateSlide = (index: number, patch: Partial<CarouselSubSlide>) => {
    const updated = [...value]
    updated[index] = { ...updated[index], ...patch }
    onChange?.(updated)
  }

  const addEmptySlide = () => {
    if (value.length >= REQUIRED_COUNT) return
    onChange?.([
      ...value,
      {
        sku: '',
        productId: 0,
        title: '',
        subtitle: '',
        productImage: '',
        displayOrder: value.length,
      },
    ])
  }

  const removeSlide = (index: number) => {
    const updated = value.filter((_, i) => i !== index)
      .map((s, i) => ({ ...s, displayOrder: i }))
    onChange?.(updated)
  }

  const handleProductSelect = (index: number, item: Item) => {
    if (!item.id) return
    // Keep any custom title the user already typed; only auto-fill when empty.
    const currentTitle = value[index]?.title?.trim()
    updateSlide(index, {
      productId: item.id,
      sku: item.sku ?? '',
      title: currentTitle ? value[index].title : item.description ?? '',
    })
  }

  const handleImageUploaded = (index: number, filename: string) => {
    updateSlide(index, { productImage: filename })
  }

  const setUploading = (index: number, uploading: boolean) => {
    setUploadingIndexes((prev) => ({ ...prev, [index]: uploading }))
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Typography.Text strong>{translate('sub_slides')}</Typography.Text>
        {value.length < REQUIRED_COUNT && (
          <Button
            size="small"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={addEmptySlide}
          >
            {translate('add')}
          </Button>
        )}
      </div>

      {value.map((sub, index) => (
        <div
          key={sub.id ?? `new-${index}`}
          style={{
            border: '1px solid #f0f0f0',
            borderRadius: 6,
            padding: 12,
            marginBottom: 8,
            background: '#fafafa',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <Typography.Text type="secondary">
              {translate('slide')} #{index + 1}
            </Typography.Text>
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => removeSlide(index)}
            />
          </div>

          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {translate('select_product')} ({translate('sku')})
              </Typography.Text>
              <Combo<Item, number>
                value={sub.productId || undefined}
                style={{ width: '100%' }}
                onChange={(val, item) => {
                  if (item && !Array.isArray(item)) {
                    handleProductSelect(index, item)
                  }
                }}
                fetch={async ({ request, search, value: lookupId }) => {
                  const { data } = await itemsFind({
                    variables: {
                      request: {
                        ...request,
                        skip: request?.skip ?? 0,
                        take: request?.take ?? 10,
                        ...(lookupId ? { id: lookupId } : {}),
                        ...(search ? { sku: search } : {}),
                        types: itemTypes,
                        showNoEffects: false,
                        showOnWeb: true,
                      },
                    },
                  })
                  const list = data?.itemsFind.data ?? []
                  return list.map((e) => ({
                    value: e.id || 0,
                    display: `${e.sku} - ${e.description}`,
                    item: e || {},
                  }))
                }}
              />
            </div>

            <div>
              <Space size={4}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {translate('title')}
                </Typography.Text>
                <Tooltip title={translate('sub_slide_title_tooltip')}>
                  <InfoCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12 }} />
                </Tooltip>
              </Space>
              <Input
                value={sub.title}
                placeholder={translate('title')}
                onChange={(e) => updateSlide(index, { title: e.target.value })}
              />
            </div>

            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {translate('product_image')}
              </Typography.Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {uploadingIndexes[index] ? (
                  <Skeleton.Image active style={{ width: 60, height: 40 }} />
                ) : (
                  sub.productImage && (
                    <Image
                      width={60}
                      height={40}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                      src={resolvePathFile(sub.productImage)}
                      preview={false}
                    />
                  )
                )}
                <ImageUpload
                  onUploaded={(filename) => handleImageUploaded(index, filename)}
                  onUploadingChange={(uploading) => setUploading(index, uploading)}
                  buttonSize="small"
                />
              </div>
            </div>
          </Space>
        </div>
      ))}

      {value.length < REQUIRED_COUNT && (
        <Typography.Text type="warning" style={{ fontSize: 12 }}>
          {translate('exactly_three_sub_slides')}
        </Typography.Text>
      )}
    </div>
  )
}
